/**
 * Header Constants
 *
 * @fileoverview
 * Constants for header design states, style mappings, and configuration.
 * Used by useHeaderState hook and HeaderContext.
 *
 * @see task-sticky-header.md for requirements
 */

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config';

/**
 * Header design states from Contentful/code configuration
 * @enum {string}
 */
export const HEADER_DESIGN_STATE = {
  TRANSPARENT: 'transparent', // Light style, transparent bg
  GRADIENT: 'gradient', // Dark style, gradient overlay
  SOLID: 'solid', // Light style, solid bg (default)
};

/**
 * Header style mapping for CSS
 * Maps to color tokens: light = grayAlpha.90/100, dark = text.white
 * @enum {string}
 */
export const HEADER_STYLE = {
  LIGHT: 'light', // Black icons/text (transparent, solid)
  DARK: 'dark', // White icons/text (gradient)
};

/**
 * URL patterns that force solid header regardless of configuration
 * - Search results page
 * PDP header styling is driven by Contentful Page fields (not path prefix).
 * @type {string[]}
 */
export const FORCE_SOLID_PAGES = [
  '/search', // Search results
];

/**
 * URL patterns that disable sticky behavior
 * - Range/Category listing pages
 * @type {string[]}
 */
export const DISABLE_STICKY_PAGES = [
  '/category/',
  '/checkout'
];

/**
 * Scroll threshold before the header becomes sticky.
 * Resting treatment only within this distance of the page top; past it = active.
 * @type {number}
 */
export const STICKY_SCROLL_THRESHOLD = 68;

/** Always show the header (resting) while scrollY is within this many px of the top. */
export const STICKY_TOP_ALWAYS_VISIBLE_PX = 68;

/**
 * Default header design state when not configured
 * @type {string}
 */
export const DEFAULT_HEADER_STATE = HEADER_DESIGN_STATE.SOLID;

/**
 * Resolve whether the header wishlist icon (desktop) and "My quote" row (mobile drawer) render.
 *
 * Controlled by `app.wishlist.headerIconEnabled` in `pwa/config/default.js` (default `false`).
 *
 * @returns {boolean}
 */
export const isHeaderWishlistIconEnabled = () =>
  getConfig()?.app?.wishlist?.headerIconEnabled === true;
